import { apiMedService } from '@/api'
import type {
  MedComplex,
  MedComplexList,
  MedComplexModel,
  MedComplexCertificate,
  MedComplexQueryParams,
  CertificateCheckInfoList,
} from '@/api/models/medcomplex'
import type { BaseQueryParams } from '@/types'

export const medcomplexApi = apiMedService.injectEndpoints({
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getMedComplexList: builder.query<MedComplexList, any>({
      query: (params) => ({
        url: `/v1/medical-complex`,
        method: 'GET',
        params,
      }),
    }),
    getMedComplexModelsList: builder.query<MedComplexModel[], void>({
      query: () => ({
        url: `/v1/medical-complex/models/get`,
        method: 'GET',
      }),
    }),
    createMedComplex: builder.mutation<void, MedComplex>({
      query: (payload) => ({
        url: `/v1/medical-complex`,
        method: 'POST',
        body: payload,
      }),
    }),
    getMedComplexById: builder.query<MedComplex, string>({
      query: (id) => ({
        url: `/v1/medical-complex/${id}`,
        method: 'GET',
      }),
    }),
    updateMedComplex: builder.mutation<
      void,
      { id: number; payload: Partial<MedComplex> }
    >({
      query: ({ id, payload }) => ({
        url: `/v1/medical-complex/${id}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    createMedComplexCertificate: builder.mutation<
      void,
      { id: number; payload: Partial<MedComplexCertificate> }
    >({
      query: ({ id, payload }) => ({
        url: `/v1/medical-complex/${id}/certificate`,
        method: 'POST',
        body: payload,
      }),
    }),
    createMedComplexFiles: builder.mutation<
      void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: number; certId?: number; payload: any }
    >({
      query: ({ id, certId, payload }) => ({
        url: `/v1/medical-complex/${id}/certificate/${certId}/files-upload`,
        method: 'POST',
        body: payload,
      }),
    }),
    updateMedComplexCertificate: builder.mutation<
      void,
      { id: number; certId?: number; payload: Partial<MedComplexCertificate> }
    >({
      query: ({ id, certId, payload }) => ({
        url: `/v1/medical-complex/${id}/certificate/${certId}`,
        method: 'PATCH',
        body: payload,
      }),
    }),
    deleteMedComplexFiles: builder.mutation<
      void,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { id: number; certId?: number; payload: any }
    >({
      query: ({ id, certId, payload }) => ({
        url: `/v1/medical-complex/${id}/certificate/${certId}/files-remove`,
        method: 'DELETE',
        body: payload,
      }),
    }),
    deleteMedComplexCertificate: builder.mutation<
      void,
      { id: number; certId: number }
    >({
      query: ({ id, certId }) => ({
        url: `/v1/medical-complex/${id}/certificate/${certId}`,
        method: 'DELETE',
      }),
    }),
    getCertificatesExpiry: builder.query<CertificateCheckInfoList, void>({
      query: () => ({
        url: `/v1/medical-complex/certificates/check`,
        method: 'GET',
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getFile: builder.query<any, string>({
      query: (url) => ({
        url: `${url}`,
        method: 'GET',
        responseHandler: async (response) =>
          window.URL.createObjectURL(await response.blob()),
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getGmtList: builder.query<any, void>({
      query: () => ({
        url: `/v1/medical-complex/gmts`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetMedComplexListQuery,
  useGetMedComplexModelsListQuery,
  useCreateMedComplexMutation,
  useGetMedComplexByIdQuery,
  useUpdateMedComplexMutation,
  useCreateMedComplexCertificateMutation,
  useCreateMedComplexFilesMutation,
  useLazyGetMedComplexByIdQuery,
  useLazyGetMedComplexListQuery,
  useLazyGetCertificatesExpiryQuery,
  useDeleteMedComplexCertificateMutation,
  useLazyGetFileQuery,
  useUpdateMedComplexCertificateMutation,
  useDeleteMedComplexFilesMutation,
  useGetGmtListQuery,
} = medcomplexApi
